import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Code2, Layers, Rocket, Settings, Sparkles, Boxes } from 'lucide-react'
import SEO from '../../components/SEO'

const DevelopmentServices = () => {
  const services = [
    {
      icon: Code2,
      title: 'Bespoke Applications',
      description: 'Custom software solutions designed exclusively for your unique requirements and workflows.'
    },
    {
      icon: Layers,
      title: 'Full-Stack Development',
      description: 'Complete development from elegant user interfaces to robust backend systems.'
    },
    {
      icon: Sparkles,
      title: 'Modern Technologies',
      description: 'Leveraging cutting-edge frameworks and tools for superior performance and scalability.'
    },
    {
      icon: Boxes,
      title: 'System Integration',
      description: 'Seamlessly connecting your custom solutions with existing systems and services.'
    },
    {
      icon: Rocket,
      title: 'Rapid Prototyping',
      description: 'Quick iteration and refinement to bring your vision to life efficiently.'
    },
    {
      icon: Settings,
      title: 'Ongoing Support',
      description: 'Continuous maintenance, updates, and enhancements as your needs evolve.'
    }
  ]

  return (
    <>
      <SEO 
        title="Development Services - Praetorian Works"
        description="Elite custom development services for HNW and UHNW clients. Bespoke applications, modern technology, and white glove development support."
        keywords="custom software development, bespoke applications, private software development, luxury tech development"
      />
      
      <div className="min-h-screen">
        {/* Hero Section */}
        <section className="relative pt-32 pb-20">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_bottom_right,_var(--tw-gradient-stops))] from-primary-900/20 via-transparent to-transparent"></div>
          
          <div className="container-max relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary-400 to-primary-600 rounded-2xl mb-8 shadow-2xl">
                <Code2 className="w-10 h-10 text-secondary-900" />
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">
                Development
                <span className="text-primary-500 ml-4">Services</span>
              </h1>
              <p className="text-xl text-neutral-300 mb-8 leading-relaxed">
                Custom software development tailored to your vision. From personal productivity tools 
                to sophisticated business applications, we create bespoke solutions that work exactly 
                the way you need them to.
              </p>
            </motion.div>
          </div>
        </section>

        {/* Services */}
        <section className="py-20">
          <div className="container-max">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 * index }}
                  className="bg-secondary-800 rounded-2xl p-8 border border-neutral-800 hover:border-primary-500/50 transition-all duration-300"
                >
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl mb-6 shadow-lg">
                    <service.icon className="w-8 h-8 text-secondary-900" />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-4">
                    {service.title}
                  </h3>
                  <p className="text-neutral-400 leading-relaxed">
                    {service.description}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Value Proposition */}
        <section className="py-20 bg-black/30">
          <div className="container-max">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="max-w-4xl mx-auto text-center"
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-8">
                Software Crafted <span className="text-primary-500">For You</span>
              </h2>
              <p className="text-lg text-neutral-300 mb-6 leading-relaxed">
                Off-the-shelf software rarely fits your exact needs. Our Development Services create 
                custom applications designed precisely for your workflows, preferences, and requirements—
                whether for business, family office management, or personal productivity.
              </p>
              <p className="text-lg text-neutral-300 leading-relaxed">
                From mobile apps to web platforms to automation tools, we bring your ideas to life 
                with elegant, powerful software that feels like it was made just for you—because it was.
              </p>
            </motion.div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20">
          <div className="container-max text-center">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Build Your Vision
              </h2>
              <p className="text-xl text-neutral-300 mb-8 max-w-2xl mx-auto">
                Let's discuss how custom software can transform your workflows and productivity.
              </p>
              <Link to="/contact" className="btn-primary text-lg">
                Start Your Project
              </Link>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  )
}

export default DevelopmentServices
